import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "./../child/Alert/SuccessAlert";
import DengerAlert from "./../child/Alert/DengerAlert";
import OtpInput from "react-otp-input";
import "./../../custom.css";

const OTPLayer = () => {
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [timer, setTimer] = useState(120);
   const [adminid, setAdminid] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      navigate("/dashboard");
    }

    document.body.classList.add("signin-page");
    return () => {
      document.body.classList.remove("signin-page");
    };
  }, [navigate]);

  // Initialize timer from localStorage or set new
  useEffect(() => {
    const savedOtpStart = localStorage.getItem("otpStartTime");

    if (savedOtpStart) {
      const elapsed = Math.floor((Date.now() - parseInt(savedOtpStart)) / 1000);
      const remaining = 120 - elapsed;

      if (remaining <= 0) {
        setTimer(0);
        setOtpExpired(true);
      } else {
        setTimer(remaining);
        setOtpExpired(false);
      }
    } else {
      const now = Date.now();
      localStorage.setItem("otpStartTime", now.toString());
      setTimer(120);
    }
  }, []);

  // Countdown logic
  useEffect(() => {
    if (timer <= 0 || otpExpired) return;

    const countdown = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [timer, otpExpired]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const validateInputs = () => {
    const newErrors = {};
    if (!otp) {
      newErrors.otp = "One Time Code is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    setLoading(true);

    const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/otpVerify`;
    const email = localStorage.getItem("email");

    if (!email) {
      setErrorMessage("Email not found. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();
       

      if (!response.ok) {
        throw new Error(data?.message || "OTP verification failed.");
      }
      localStorage.setItem("adminId",data.data._id);
      setSuccessMessage("OTP verified successfully! Redirecting...");
      localStorage.removeItem("otpStartTime"); // Clear timer on success
      setTimeout(() => {
        setSuccessMessage("");
        navigate("/resetpassword");
      }, 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    const email = localStorage.getItem("email");

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/resendOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to resend OTP.");
      }

      setOtp("");
      const now = Date.now();
      localStorage.setItem("otpStartTime", now.toString());
      setOtpExpired(false);
      setTimer(120);
      setSuccessMessage("OTP resent successfully.");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      {successMessage && (
        <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />
      )}
      {errorMessage && (
        <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />
      )}

      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img
            src={`${process.env.REACT_APP_PUBLIC_UR}/assets/images/auth/otp-screen.png`}
            alt="Auth"
          />
        </div>
      </div>

      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">
          <div>
            <img
              src={`${process.env.REACT_APP_PUBLIC_UR}/assets/images/logo.png`}
              alt="Logo"
            />
            <h4 className="mb-12">OTP Verification</h4>
            <p className="mb-32 text-secondary-light text-lg">
              Enter your OTP to continue.
            </p>
          </div>

          <form onSubmit={handleOtp}>
            <div className="d-flex justify-content-center mb-3">
              <OtpInput
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  setErrors((prev) => ({ ...prev, otp: "" }));
                }}
                numInputs={4}
                renderSeparator={<span>-</span>}
                renderInput={(props) => <input {...props} className="otp-input" />}
                shouldAutoFocus
                isDisabled={otpExpired}
              />
            </div>

            {errors.otp && <p className="text-danger mt-1">{errors.otp}</p>}

            {!otpExpired ? (
              <p className="text-secondary text-center">Expires in {formatTime(timer)}</p>
            ) : (
              <p className="text-danger text-center">OTP expired.</p>
            )}

            <button
              type="submit"
              className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
              disabled={loading || otpExpired}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>

            {otpExpired && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary"
                  onClick={handleResendOTP}
                  disabled={resendLoading}
                >
                  {resendLoading ? "Resending..." : "Resend OTP"}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default OTPLayer;
